import { useMemo, useState } from 'react';
import { ABSTAIN, WHISPER_EMOTES } from '@ringmaster/shared';
import type {
  PrivateView,
  PublicView,
  VoteAction,
  WhisperEmote,
  WhisperPrivate,
  WhisperPublic,
} from '@ringmaster/shared';
import { useRoom } from '../net/useRoom.js';
import { FixedActionBar, fixedPrimaryButtonStyle } from '../ui/FixedActionBar.js';
import { Glyph } from '../ui/Glyph.js';
import { HoloRoleCard } from '../ui/HoloRoleCard.js';
import { roleCardDefFor, WHISPER_CARD_BACK } from '../ui/roleCardDefs.js';
import { WhisperTally } from '../ui/WhisperRoleTally.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';
import { WHISPER_CHIP_LABELS, WHISPER_MAX_CHARS, WHISPER_PENDING_CAP } from './whisperChips.js';
import waxSeal from '../assets/ornaments/wax-seal.webp';

/**
 * The Whisper — Hand (per-phone) screens. Routed from HandGameView by gameId +
 * phase (THE_WHISPER_SPEC.md §11). Mystical arcana/purple séance skin (IM FELL
 * display, --arcana-* palette, seerEye/candle glyphs) — never a cartoon look.
 *
 * Screens:
 *  - WhisperDealScreen   (DEAL / WHISPER_INTRO role reveal)
 *  - WhisperQuestionScreen (QUESTION: type box + chips + emote row + submit;
 *                           Paladin "stay their hand" tap-a-ring-player)
 *  - WhisperHuntScreen   (HUNT: the lynch "seal" ceremony → SUBMIT_VOTE)
 *
 * The Whisper is a one-round game — the engine never enters SCORE
 * (RESOLVE_HUNT → END directly); there is no score screen. `whisperLeaderboard`
 * is still exported below — the Stage (`stage/WhisperStage.tsx`) uses it.
 *
 * Reads state from `privateView.whisper` (WhisperPrivate: theWord for Deceiver,
 * paladinCharge) and `publicView.whisper` (WhisperPublic: ring, candleWaxPct,
 * runes). Both are optional blocks from the contract. NOT `whisper.scores` —
 * see `whisperLeaderboard` for why that field is legacy.
 */

const ARCANA_BG = 'radial-gradient(120% 90% at 50% 0%, #1e0f34 0%, #120a24 55%, #080511 100%)';
const ARCANA_BG_FLAT = 'linear-gradient(180deg, #080808 0%, #160b26 100%)';

const EMOTE_LABEL: Record<WhisperEmote, string> = {
  laugh: '😂',
  think: '🤔',
  eyes: '👀',
};

// ─── DEAL / role reveal ──────────────────────────────────────────────────────

/**
 * WhisperDealScreen — the DEAL beat, mirroring Blood Moon's deal exactly:
 * roles + the Deceiver's word are dealt on DEAL ENTRY (engine), so the phone
 * shows the SAME press-and-hold HoloRoleCard ceremony Blood Moon uses
 * (hand/RoleRevealScreen.tsx): def from `roleCardDefFor('whisper', role)`,
 * arcana card back (`WHISPER_CARD_BACK`). The READY button appears only AFTER
 * the card is flipped — you can't ready up without knowing your part. The
 * reveal is controlled (sticky once flipped) so the card never flips back:
 *   · Deceiver — `theWord` as an ember/sigil below the card (their secret).
 *   · Paladin  — one-charge note below the card.
 *   · Villager — just the card.
 * Beneath the ceremony (visible before AND after the flip) sits a compact
 * "tonight's table" line — dynamic prose built from the ACTUAL role counts
 * for this table size (`whisperDistribution`, shared/games.ts), so a
 * 4-player game reads differently from a 7-player one. Mirrors the desktop
 * `WhisperReveal.tsx`'s table line exactly.
 * WHISPER_INTRO afterwards is a pure ritual beat (WhisperWaitScreen).
 */
export function WhisperDealScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const send = useRoom((s) => s.send);
  const w = privateView.whisper;
  const role = privateView.role;
  const [revealed, setRevealed] = useState(false);

  const serverReady = privateView.action.type === 'READY' && privateView.action.done;
  const [localReady, setLocalReady] = useState(false);
  const isReady = serverReady || localReady;

  function handleReady() {
    setLocalReady(true);
    send({ type: 'READY' });
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: ARCANA_BG,
        padding: 'var(--space-5)',
        paddingBottom: 120 + actionOffset,
        gap: 'var(--space-4)',
        overflow: 'hidden',
        textAlign: 'center',
      }}
    >
      <ParticleLayer preset="arcanaWisps" intensity={0.6} slot={0} />

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)', maxWidth: 340 }}>
        <WhisperRevealCeremony role={role} whisper={w} revealed={revealed} onReveal={() => setRevealed(true)} />
        <WhisperTally playerCount={publicView.players.length} />

        {isReady && (
          <p style={{ fontFamily: 'var(--font-ui)', fontSize: 14, color: 'var(--text-tertiary)', margin: 0 }}>
            Waiting for the others…
          </p>
        )}
      </div>

      {revealed && !isReady && (
        <FixedActionBar offsetBottom={actionOffset}>
          <button type="button" className="rm-hover-pill" onClick={handleReady} style={fixedPrimaryButtonStyle}>
            I&rsquo;m ready
          </button>
        </FixedActionBar>
      )}
    </div>
  );
}


/**
 * WhisperRevealCeremony — the press-and-hold HoloRoleCard flip (mirrors the
 * desktop `WhisperReveal.tsx`'s `RevealCeremony` and Blood Moon's
 * `RoleRevealScreen`). Reveal is controlled here (not the card's internal
 * state) purely so the caption ("Hold to reveal" vs the Seer's opening line)
 * and the below-card plaques can react to it.
 */
function WhisperRevealCeremony({
  role,
  whisper,
  revealed,
  onReveal,
}: {
  role: PrivateView['role'];
  whisper?: WhisperPrivate;
  revealed: boolean;
  onReveal: () => void;
}) {
  const def = roleCardDefFor('whisper', role);
  return (
    <>
      <HoloRoleCard
        def={def}
        backSrc={WHISPER_CARD_BACK}
        revealed={revealed}
        onReveal={onReveal}
        width="min(280px, 78vw, 32vh)"
      />
      {revealed && role === 'DECEIVER' && (
        <div className="whisper-word-ember">
          <span className="whisper-word-ember-label">The word is</span>
          <span className="whisper-word-ember-word">{whisper?.theWord ?? '—'}</span>
        </div>
      )}
      {revealed && role === 'PALADIN' && <PaladinChargePill whisper={whisper} />}
      <WhisperHandStyles />
    </>
  );
}

function PaladinChargePill({ whisper }: { whisper?: WhisperPrivate }) {
  const charged = whisper?.paladinCharge ?? true;
  return (
    <div
      style={{
        fontFamily: 'var(--font-ui)',
        fontSize: 13,
        fontWeight: 600,
        color: charged ? 'var(--arcana-4, #c9b6ff)' : 'var(--text-tertiary)',
        border: `1px solid ${charged ? 'rgba(139,92,246,0.5)' : 'var(--surface-stroke)'}`,
        borderRadius: 'var(--radius-pill)',
        padding: '4px 14px',
      }}
    >
      {charged ? 'Charge ready — stay a suspect’s hand once this round' : 'Charge spent'}
    </div>
  );
}

// ─── Ritual / transient waiting beats (WHISPER_INTRO / RESOLVE_*) ────────────

/**
 * WhisperWaitScreen — the calm arcana "listen to the Seer" beat for phases where
 * the phone has no action: the ritual intro and the resolve/reveal transients.
 * Renders the current narration caption (the Seer speaking) over drifting wisps.
 */
export function WhisperWaitScreen({ caption, title }: { caption?: string; title: string }) {
  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: ARCANA_BG,
        padding: 'var(--space-6)',
        gap: 'var(--space-4)',
        overflow: 'hidden',
        textAlign: 'center',
      }}
    >
      <WhisperHandStyles />
      <ParticleLayer preset="arcanaWisps" intensity={0.7} slot={0} />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-3)', maxWidth: 340 }}>
        <Glyph name="seerEye" size={48} tone="arcana" />
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, margin: 0, color: 'var(--arcana-4, #c9b6ff)', letterSpacing: '0.03em' }}>
          {title}
        </h1>
        {caption && (
          <p style={{ fontFamily: 'var(--font-ui)', fontSize: 15, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
            {caption}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── QUESTION — the free-type queue ──────────────────────────────────────────

/**
 * WhisperQuestionScreen — the heart of the Hand. A ≤30-char text box + standard
 * question chips + an emote row + a Submit button. Submit is disabled when this
 * player has hit the pending cap (>= 3) or is frozen (Paladin hold). The Paladin
 * additionally gets a "stay their hand" mode: tap a ring player → PALADIN_HOLD.
 */
export function WhisperQuestionScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const send = useRoom((s) => s.send);
  const w = publicView.whisper;
  const priv = privateView.whisper;
  const [text, setText] = useState('');
  const [holdMode, setHoldMode] = useState(false);

  const myId = privateView.playerId;
  const myRing = w?.ring.find((r) => r.playerId === myId);
  const pending = myRing?.pending ?? 0;
  const frozen = myRing?.frozen ?? false;
  const atCap = pending >= WHISPER_PENDING_CAP;
  const canSubmit = text.trim().length > 0 && !atCap && !frozen;

  const isPaladin = privateView.role === 'PALADIN';
  const paladinCharged = priv?.paladinCharge ?? false;
  const nameMap = useMemo(
    () => Object.fromEntries(publicView.players.map((p) => [p.id, p.name])),
    [publicView.players],
  );

  function submit() {
    const t = text.trim();
    if (!t || atCap || frozen) return;
    send({ type: 'SUBMIT_WHISPER', text: t.slice(0, WHISPER_MAX_CHARS) });
    setText('');
  }

  function tapChip(label: string) {
    // A chip is a one-tap question — send it straight (respecting the cap/freeze).
    if (atCap || frozen) return;
    send({ type: 'SUBMIT_WHISPER', text: label.slice(0, WHISPER_MAX_CHARS) });
  }

  function tapEmote(emote: WhisperEmote) {
    send({ type: 'EMOTE', emote });
  }

  function tapHoldTarget(targetId: string) {
    if (!paladinCharged) return;
    send({ type: 'PALADIN_HOLD', targetId });
    setHoldMode(false);
  }

  // Paladin "stay their hand" target picker — tap a ring player.
  if (holdMode) {
    const targets = (w?.ring ?? []).filter((r) => r.playerId !== myId);
    return (
      <div style={questionOuter(actionOffset)}>
        <WhisperHandStyles />
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-2)' }}>
            <Glyph name="candle" size={32} tone="arcana" />
          </div>
          <h1 style={questionTitle}>Stay whose hand?</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 'var(--space-2) 0 0' }}>
            Wipes their queue and freezes them. You have one charge this round.
          </p>
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', flex: 1 }}>
          {targets.map((r) => (
            <li key={r.playerId}>
              <button
                type="button"
                className="whisper-plaque"
                onClick={() => tapHoldTarget(r.playerId)}
              >
                <span>{nameMap[r.playerId] ?? r.playerId}</span>
                {r.pending > 0 && <span className="whisper-plaque-pending">{r.pending} queued</span>}
              </button>
            </li>
          ))}
        </ul>
        <FixedActionBar offsetBottom={actionOffset}>
          <button
            type="button"
            className="rm-hover-pill"
            onClick={() => setHoldMode(false)}
            style={{ ...fixedPrimaryButtonStyle, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--surface-stroke)' }}
          >
            Never mind
          </button>
        </FixedActionBar>
      </div>
    );
  }

  return (
    <div style={questionOuter(actionOffset)}>
      <WhisperHandStyles />

      {/* Prompt */}
      <div style={{ textAlign: 'center' }}>
        <h1 style={questionTitle}>Ask the Seer</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 'var(--space-2) 0 0' }}>
          Type a question or a guess. She reads them one at a time.
        </p>
      </div>

      {/* Deceiver's secret reminder — the word they alone can see. */}
      {privateView.role === 'DECEIVER' && priv?.theWord && (
        <div className="whisper-deceiver-reminder">
          <Glyph name="seerEye" size={16} tone="arcana" />
          <span>The word is <strong>{priv.theWord}</strong> — steer them away.</span>
        </div>
      )}

      {/* Frozen / cap notices */}
      {frozen ? (
        <div className="whisper-notice whisper-notice-frozen">
          <Glyph name="candle" size={16} tone="arcana" />
          <span>A guardian has stayed your hand. You can&rsquo;t submit right now.</span>
        </div>
      ) : atCap ? (
        <div className="whisper-notice">
          Your queue is full ({WHISPER_PENDING_CAP}). Wait for the Seer to read one.
        </div>
      ) : null}

      {/* Text box */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <input
            type="text"
            value={text}
            maxLength={WHISPER_MAX_CHARS}
            disabled={frozen}
            placeholder={frozen ? 'Frozen…' : 'is it alive?  ·  moth'}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            className="whisper-input"
            aria-label="Your question or guess"
          />
          <button
            type="button"
            className="whisper-submit"
            disabled={!canSubmit}
            onClick={submit}
            style={{
              background: canSubmit ? 'var(--arcana-3, #8b5cf6)' : 'rgba(255,255,255,0.08)',
              color: canSubmit ? '#fff' : 'var(--text-tertiary)',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            Send
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-tertiary)', fontFamily: 'var(--font-ui)' }}>
          <span>{pending} / {WHISPER_PENDING_CAP} queued</span>
          <span>{text.length} / {WHISPER_MAX_CHARS}</span>
        </div>
      </div>

      {/* Standard question chips */}
      <div>
        <div style={sectionLabel}>Quick questions</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {WHISPER_CHIP_LABELS.map((label) => (
            <button
              key={label}
              type="button"
              className="whisper-chip"
              disabled={atCap || frozen}
              onClick={() => tapChip(label)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Emote row */}
      <div>
        <div style={sectionLabel}>React</div>
        <div style={{ display: 'flex', gap: 10 }}>
          {WHISPER_EMOTES.map((emote) => (
            <button
              key={emote}
              type="button"
              className="whisper-emote"
              onClick={() => tapEmote(emote)}
              aria-label={`React ${emote}`}
            >
              {EMOTE_LABEL[emote]}
            </button>
          ))}
        </div>
      </div>

      {/* Paladin power — fixed at bottom */}
      {isPaladin && (
        <FixedActionBar offsetBottom={actionOffset}>
          <button
            type="button"
            className="rm-hover-pill"
            disabled={!paladinCharged}
            onClick={() => setHoldMode(true)}
            style={{
              ...fixedPrimaryButtonStyle,
              background: paladinCharged ? 'var(--arcana-3, #8b5cf6)' : 'rgba(255,255,255,0.08)',
              color: paladinCharged ? '#fff' : 'var(--text-tertiary)',
              cursor: paladinCharged ? 'pointer' : 'not-allowed',
            }}
          >
            {paladinCharged ? 'Stay a suspect’s hand' : 'Charge spent this round'}
          </button>
        </FixedActionBar>
      )}
    </div>
  );
}

// ─── HUNT — the lynch "seal" ceremony ────────────────────────────────────────

/**
 * WhisperHuntScreen — the Deceiver accusation. A faithful clone of the Blood
 * Moon lynch ceremony (`hand/VoteScreen.tsx`): engraved plaques with radio
 * dots, a fixed action bar (abstain "Name no one" + a wax-seal confirm), and a
 * sealed-ballot locked state with the stamping pop + a short haptic. Re-tinted
 * blood → arcana throughout (--arcana-3/-4 selection states, radio fill,
 * glows). Network contract is byte-identical to VoteScreen: tap sets
 * `pending`, Confirm sends `{ type: 'SUBMIT_VOTE', targetId: pending ===
 * ABSTAIN ? ABSTAIN : pending }` and sets `locked` (ABSTAIN constant, same as
 * Blood Moon's vote).
 */
export function WhisperHuntScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const send = useRoom((s) => s.send);
  const action = privateView.action.type === 'VOTE' ? (privateView.action as VoteAction) : null;
  const [pending, setPending] = useState<string | typeof ABSTAIN | null>(action?.myVote ?? null);
  const [locked, setLocked] = useState(action?.myVote != null);
  const [stamping, setStamping] = useState(false);

  const nameMap = useMemo(
    () => Object.fromEntries(publicView.players.map((p) => [p.id, p.name])),
    [publicView.players],
  );
  const candidates = action?.candidates ?? [];

  function handleConfirm() {
    if (!pending) return;
    // ── Network contract (unchanged from the Blood Moon vote): ──
    send({ type: 'SUBMIT_VOTE', targetId: pending === ABSTAIN ? ABSTAIN : pending });
    setLocked(true);
    // Ceremony only — a stamp flourish + a short haptic on the seal press.
    setStamping(true);
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(30);
      } catch {
        /* haptics unsupported — silent */
      }
    }
  }

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: ARCANA_BG_FLAT,
        padding: 'var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-5))',
        paddingBottom: locked ? 'var(--space-5)' : 176 + actionOffset,
        gap: 'var(--space-5)',
      }}
    >
      <WhisperHandStyles />

      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-2)' }}>
          <Glyph name="seerEye" size={36} tone="arcana" />
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, margin: '0 0 var(--space-2)', letterSpacing: '0.02em', color: 'var(--arcana-4, #c9b6ff)' }}>
          Who was the Deceiver?
        </h1>
        {publicView.voteProgress && (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 'var(--space-2) 0 0' }}>
            {publicView.voteProgress.voted} / {publicView.voteProgress.total} named
          </p>
        )}
      </div>

      {locked ? (
        <WhisperSealedBallot pending={pending} nameMap={nameMap} stamping={stamping} />
      ) : (
        <>
          {/* Candidate plaques */}
          <div style={{ flex: 1 }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {candidates.map((id) => {
                const pName = nameMap[id] ?? id;
                const isSelected = pending === id;
                const isMe = id === privateView.playerId;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => setPending(id)}
                      className={`whisper-vote-plaque${isSelected ? ' is-selected' : ''}`}
                      aria-pressed={isSelected}
                    >
                      <span
                        className="whisper-vote-radio"
                        aria-hidden
                        style={{
                          borderColor: isSelected ? 'var(--arcana-3, #8b5cf6)' : 'var(--surface-stroke)',
                          background: isSelected ? 'var(--arcana-3, #8b5cf6)' : 'transparent',
                        }}
                      />
                      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                        {pName}
                        {isMe && <span style={{ color: 'var(--text-tertiary)', fontSize: 13, fontWeight: 500 }}>(you)</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Abstain + seal-preview confirm — fixed, always visible */}
          <FixedActionBar offsetBottom={actionOffset}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <button
                type="button"
                onClick={() => setPending(ABSTAIN)}
                className={`whisper-vote-abstain${pending === ABSTAIN ? ' is-selected' : ''}`}
              >
                <Glyph name="scales" size={18} tone="bone" />
                <span>Name no one</span>
              </button>
              <button
                type="button"
                disabled={!pending}
                onClick={handleConfirm}
                className="whisper-vote-confirm"
                style={{
                  background: pending ? 'var(--arcana-3, #8b5cf6)' : 'rgba(255,255,255,0.1)',
                  color: pending ? '#fff' : 'var(--text-tertiary)',
                  cursor: pending ? 'pointer' : 'not-allowed',
                  boxShadow: pending ? '0 0 24px rgba(139,92,246,0.5)' : 'none',
                }}
              >
                {/* Selecting a target slides a wax-seal ballot preview into the bar. */}
                {pending && pending !== ABSTAIN && (
                  <img src={waxSeal} alt="" aria-hidden className="whisper-vote-confirm-seal" />
                )}
                <span>
                  {pending
                    ? pending === ABSTAIN
                      ? 'Seal your accusation: no one'
                      : `Seal your accusation: ${nameMap[pending as string] ?? pending}`
                    : 'Choose someone'}
                </span>
              </button>
            </div>
          </FixedActionBar>
        </>
      )}
    </div>
  );
}

function WhisperSealedBallot({
  pending,
  nameMap,
  stamping,
}: {
  pending: string | typeof ABSTAIN | null;
  nameMap: Record<string, string>;
  stamping: boolean;
}) {
  const label =
    pending === ABSTAIN
      ? 'You named no one'
      : pending
        ? `You named ${nameMap[pending] ?? pending}`
        : 'Your accusation is sealed';
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="whisper-vote-sealed">
        <div className={`whisper-vote-sealed-seal${stamping ? ' is-stamping' : ''}`}>
          <img src={waxSeal} alt="" aria-hidden style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 20, margin: '0 0 var(--space-2)', letterSpacing: '0.03em', color: 'var(--arcana-4, #c9b6ff)' }}>
          Your accusation is sealed
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: 0 }}>{label}</p>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 'var(--space-3) 0 0' }}>
          Awaiting the others&hellip;
        </p>
      </div>
    </div>
  );
}

// ─── Leaderboard helper (kept — the Stage uses it; no score screen anymore) ──

/**
 * Build a sorted leaderboard from the ROOM's cumulative totals.
 *
 * Reads `players[].score`, NEVER `whisper.scores`. The whisper block's scores
 * are ENGINE-scoped — they reset when the room switches game — while
 * `players[].score` is the room total that survives a switch (the same number
 * the end screens and the score reveal show). Rendering both put two different
 * numbers for the same player on the same night; `whisper.scores` is legacy and
 * is deliberately not read anywhere in the client.
 */
export function whisperLeaderboard(
  view: PublicView,
): { id: string; name: string; score: number }[] {
  return view.players
    .map((p) => ({ id: p.id, name: p.name, score: p.score }))
    .sort((a, b) => b.score - a.score);
}

// ─── Shared layout helpers + scoped styles ───────────────────────────────────

function questionOuter(actionOffset: number): React.CSSProperties {
  return {
    minHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    background: ARCANA_BG_FLAT,
    padding: 'var(--space-5)',
    paddingTop: 'max(var(--space-6), env(safe-area-inset-top, 0px) + var(--space-4))',
    paddingBottom: 176 + actionOffset,
    gap: 'var(--space-4)',
  };
}

const questionTitle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 26,
  margin: 0,
  color: 'var(--arcana-4, #c9b6ff)',
  letterSpacing: '0.02em',
};

const sectionLabel: React.CSSProperties = {
  fontFamily: 'var(--font-ui)',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-tertiary)',
  marginBottom: 'var(--space-2)',
};

function WhisperHandStyles() {
  return (
    <style>{`
      .whisper-word-ember {
        margin-top: var(--space-2);
        display: flex; flex-direction: column; align-items: center; gap: 4px;
        padding: var(--space-4) var(--space-6);
        border-radius: var(--radius-card);
        border: 1px solid rgba(139,92,246,0.5);
        background: radial-gradient(120% 120% at 50% 0%, rgba(139,92,246,0.28) 0%, rgba(22,11,38,0.6) 100%);
        box-shadow: 0 0 40px rgba(139,92,246,0.35);
      }
      .whisper-word-ember-label {
        font-family: var(--font-ui); font-size: 12px; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--arcana-4, #c9b6ff);
      }
      .whisper-word-ember-word {
        font-family: var(--font-display); font-size: 40px; line-height: 1.1;
        color: #fff; text-shadow: 0 0 24px rgba(201,182,255,0.8);
      }

      .whisper-input {
        flex: 1; min-width: 0; height: 52px; padding: 0 16px;
        border-radius: var(--radius-md);
        border: 1px solid var(--surface-stroke);
        background: rgba(22,11,38,0.6);
        color: var(--text-primary);
        font-family: var(--font-ui); font-size: 16px;
      }
      .whisper-input::placeholder { color: var(--text-tertiary); }
      .whisper-input:focus { outline: none; border-color: var(--arcana-3, #8b5cf6); box-shadow: 0 0 0 3px rgba(139,92,246,0.25); }
      .whisper-input:disabled { opacity: 0.5; }

      .whisper-submit {
        flex-shrink: 0; height: 52px; padding: 0 20px; border: none;
        border-radius: var(--radius-md); font-family: var(--font-ui);
        font-weight: 600; font-size: 15px;
        transition: filter var(--duration-fast) var(--ease-standard);
      }
      .whisper-submit:not(:disabled):active { filter: brightness(0.9); }

      .whisper-chip {
        padding: 9px 14px; border-radius: var(--radius-pill);
        border: 1px solid rgba(139,92,246,0.35);
        background: rgba(139,92,246,0.1);
        color: var(--text-primary); font-family: var(--font-ui); font-size: 14px;
        cursor: pointer;
        transition: background var(--duration-fast) var(--ease-standard), transform var(--duration-fast) var(--ease-standard);
      }
      .whisper-chip:disabled { opacity: 0.4; cursor: not-allowed; }
      .whisper-chip:not(:disabled):active { transform: scale(0.97); }
      @media (hover: hover) and (pointer: fine) {
        .whisper-chip:not(:disabled):hover { background: rgba(139,92,246,0.22); }
      }

      .whisper-emote {
        width: 52px; height: 52px; border-radius: 50%;
        border: 1px solid var(--surface-stroke);
        background: rgba(22,11,38,0.6);
        font-size: 24px; line-height: 1; cursor: pointer;
        display: inline-flex; align-items: center; justify-content: center;
        transition: transform var(--duration-fast) var(--ease-standard), background var(--duration-fast) var(--ease-standard);
      }
      .whisper-emote:active { transform: scale(0.9); }
      @media (hover: hover) and (pointer: fine) {
        .whisper-emote:hover { background: rgba(139,92,246,0.18); }
      }

      .whisper-notice {
        display: flex; align-items: center; gap: 8px;
        padding: 10px 14px; border-radius: var(--radius-md);
        background: rgba(255,255,255,0.05); border: 1px solid var(--surface-stroke);
        color: var(--text-secondary); font-family: var(--font-ui); font-size: 14px;
      }
      .whisper-notice-frozen {
        background: rgba(139,92,246,0.12); border-color: rgba(139,92,246,0.4);
        color: var(--arcana-4, #c9b6ff);
      }

      .whisper-deceiver-reminder {
        display: flex; align-items: center; gap: 8px;
        padding: 10px 14px; border-radius: var(--radius-md);
        background: radial-gradient(120% 120% at 0% 0%, rgba(139,92,246,0.22) 0%, rgba(22,11,38,0.5) 100%);
        border: 1px solid rgba(139,92,246,0.45);
        color: var(--arcana-4, #c9b6ff); font-family: var(--font-ui); font-size: 14px;
      }
      .whisper-deceiver-reminder strong { color: #fff; }

      .whisper-plaque {
        width: 100%; min-height: 56px; text-align: left;
        padding: 15px var(--space-4); border-radius: var(--radius-md);
        border: 1px solid var(--surface-stroke);
        background: linear-gradient(180deg, rgba(44,22,80,0.5) 0%, rgba(22,11,38,0.7) 100%);
        color: var(--text-primary); cursor: pointer;
        font-family: var(--font-ui); font-size: 16px;
        display: flex; align-items: center; justify-content: space-between; gap: var(--space-3);
        transition: border-color var(--duration-fast) var(--ease-standard), background var(--duration-fast) var(--ease-standard), transform var(--duration-fast) var(--ease-standard);
      }
      .whisper-plaque.is-selected {
        border-color: var(--arcana-3, #8b5cf6);
        background: linear-gradient(180deg, rgba(139,92,246,0.3) 0%, rgba(44,22,80,0.5) 100%);
        box-shadow: 0 0 24px rgba(139,92,246,0.5);
      }
      .whisper-plaque:active { transform: scale(0.99); }
      @media (hover: hover) and (pointer: fine) {
        .whisper-plaque:hover:not(.is-selected) { border-color: rgba(139,92,246,0.5); transform: translateY(-1px); }
      }
      .whisper-plaque-pending {
        font-size: 13px; color: var(--arcana-4, #c9b6ff);
        background: rgba(139,92,246,0.2); border-radius: var(--radius-pill); padding: 3px 10px;
      }

      .whisper-abstain {
        width: 100%; min-height: 46px; padding: 10px var(--space-4);
        border-radius: var(--radius-md); border: 1px solid var(--surface-stroke);
        background: rgba(22,11,38,0.6); color: var(--text-secondary); cursor: pointer;
        font-family: var(--font-ui); font-size: 15px;
        display: flex; align-items: center; justify-content: center; gap: var(--space-2);
        transition: all var(--duration-fast) var(--ease-standard);
      }
      .whisper-abstain.is-selected { border-color: rgba(255,255,255,0.5); background: rgba(255,255,255,0.08); color: var(--text-primary); }

      /* ── HUNT — the lynch "seal" ceremony, re-tinted arcana ── */
      .whisper-vote-plaque {
        width: 100%; min-height: 56px; text-align: left;
        padding: 15px var(--space-4);
        border-radius: var(--radius-md);
        border: 1px solid var(--surface-stroke);
        background: linear-gradient(180deg, rgba(44,22,80,0.5) 0%, rgba(22,11,38,0.7) 100%);
        color: var(--text-primary);
        cursor: pointer; font-family: var(--font-ui); font-size: 16px;
        display: flex; align-items: center; gap: var(--space-3);
        box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
        transition: border-color var(--duration-fast) var(--ease-standard), background var(--duration-fast) var(--ease-standard), transform var(--duration-fast) var(--ease-standard);
      }
      .whisper-vote-plaque.is-selected {
        border-color: var(--arcana-3, #8b5cf6);
        background: linear-gradient(180deg, rgba(139,92,246,0.3) 0%, rgba(44,22,80,0.5) 100%);
        box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 0 24px rgba(139,92,246,0.5);
      }
      .whisper-vote-plaque:active { transform: scale(0.99); }
      @media (hover: hover) and (pointer: fine) {
        .whisper-vote-plaque:hover:not(.is-selected) { border-color: rgba(139,92,246,0.5); transform: translateY(-1px); }
        .whisper-vote-abstain:hover:not(.is-selected) { border-color: rgba(255,255,255,0.35); background: rgba(255,255,255,0.05); }
        .whisper-vote-confirm:hover:not(:disabled) { filter: brightness(0.9); transform: scale(1.02); }
      }
      .whisper-vote-radio {
        width: 20px; height: 20px; border-radius: 50%;
        border: 2px solid var(--surface-stroke);
        display: inline-block; flex-shrink: 0;
        transition: all var(--duration-fast) var(--ease-standard);
      }
      .whisper-vote-abstain {
        width: 100%; min-height: 46px; padding: 10px var(--space-4);
        border-radius: var(--radius-md);
        border: 1px solid var(--surface-stroke);
        background: rgba(22,11,38,0.6);
        color: var(--text-secondary); cursor: pointer;
        font-family: var(--font-ui); font-size: 15px;
        display: flex; align-items: center; justify-content: center; gap: var(--space-2);
        transition: all var(--duration-fast) var(--ease-standard);
      }
      .whisper-vote-abstain.is-selected {
        border-color: rgba(255,255,255,0.5);
        background: rgba(255,255,255,0.08);
        color: var(--text-primary);
      }
      .whisper-vote-confirm {
        width: 100%; font-family: var(--font-ui); font-weight: 600; font-size: 16px;
        border-radius: var(--radius-pill); padding: 16px 24px; border: none;
        min-height: 52px; position: relative; overflow: hidden;
        display: flex; align-items: center; justify-content: center; gap: 10px;
        transition: background var(--duration-base) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard), filter var(--duration-fast) var(--ease-standard), transform var(--duration-fast) var(--ease-standard);
      }
      .whisper-vote-confirm-seal { width: 26px; height: 26px; object-fit: contain; animation: whisper-vote-seal-slide 0.32s var(--ease-spring); }
      @keyframes whisper-vote-seal-slide { from { opacity: 0; transform: translateX(-12px) scale(0.6); } to { opacity: 1; transform: none; } }

      .whisper-vote-sealed { width: 100%; text-align: center; padding: var(--space-6) var(--space-5);
        background: linear-gradient(180deg, rgba(22,11,38,0.86) 0%, rgba(8,5,17,0.92) 100%);
        border: 1px solid var(--surface-stroke); border-radius: var(--radius-card);
        box-shadow: var(--shadow-card); }
      .whisper-vote-sealed-seal { width: 84px; height: 84px; margin: 0 auto var(--space-4);
        filter: drop-shadow(0 6px 14px rgba(0,0,0,0.6)); }
      .whisper-vote-sealed-seal.is-stamping { animation: whisper-vote-stamp 0.42s var(--ease-dramatic); }
      @keyframes whisper-vote-stamp {
        0% { transform: scale(1.9) rotate(-8deg); opacity: 0; }
        55% { transform: scale(0.92) rotate(2deg); opacity: 1; }
        100% { transform: none; opacity: 1; }
      }

      @media (prefers-reduced-motion: reduce) {
        .whisper-vote-confirm-seal, .whisper-vote-sealed-seal.is-stamping { animation: none; }
      }
    `}</style>
  );
}
